import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, ArrowLeft, HelpCircle, Send, MessageSquare, Clock, CheckCircle2, AlertCircle, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Support() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', category: 'general' });
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  // Contact form for non-logged-in users
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', category: 'general' });

  useEffect(() => {
    if (user) {
      axios.get(`${API}/support/tickets`, { withCredentials: true })
        .then(r => setTickets(r.data.tickets)).catch(console.error);
    }
  }, [user]);

  const submitTicket = async (e) => {
    e.preventDefault(); setLoading(true); setSuccess('');
    try {
      await axios.post(`${API}/support/tickets`, form, { withCredentials: true });
      setSuccess('Ticket submitted! We\'ll respond soon.');
      setShowNew(false); setForm({ subject: '', message: '', category: 'general' });
      const { data } = await axios.get(`${API}/support/tickets`, { withCredentials: true });
      setTickets(data.tickets);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const submitContact = async (e) => {
    e.preventDefault(); setLoading(true); setSuccess('');
    try {
      await axios.post(`${API}/support/contact`, contactForm);
      setSuccess('Message sent! We\'ll respond to your email shortly.');
      setContactForm({ name: '', email: '', message: '', category: 'general' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, { message: replyText }, { withCredentials: true });
      setReplyText('');
      const { data } = await axios.get(`${API}/support/tickets/${selectedTicket.id}`, { withCredentials: true });
      setSelectedTicket(data);
    } catch (err) { console.error(err); }
  };

  const viewTicket = async (ticket) => {
    try {
      const { data } = await axios.get(`${API}/support/tickets/${ticket.id}`, { withCredentials: true });
      setSelectedTicket(data);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Back</Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#D97757] flex items-center justify-center">
            <HelpCircle size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Help & Support</h1>
            <p className="text-[#1C2A24]/60 text-sm">Get help with your account, billing, refunds, or technical issues</p>
          </div>
        </div>

        {success && <div className="bg-[#A7F3D0]/30 text-[#047857] px-4 py-3 rounded-2xl text-sm mb-6 flex items-center gap-2" data-testid="support-success"><CheckCircle2 size={14} />{success}</div>}

        {user ? (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Ticket List */}
            <div className="md:col-span-1 space-y-3">
              <Button onClick={() => { setShowNew(true); setSelectedTicket(null); }} className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white mb-3" data-testid="new-ticket-btn">
                <Send size={14} className="mr-2" /> New Ticket
              </Button>
              {tickets.length === 0 ? (
                <p className="text-center text-[#1C2A24]/40 py-8 text-sm">No tickets yet</p>
              ) : tickets.map(t => (
                <div key={t.id} onClick={() => { viewTicket(t); setShowNew(false); }}
                  className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all ${selectedTicket?.id === t.id ? 'border-[#047857]' : 'border-[#E5E0D8] hover:border-[#047857]/30'}`}
                  data-testid={`ticket-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{t.subject}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.status === 'open' ? 'bg-[#D97757]/10 text-[#D97757]' : t.status === 'resolved' ? 'bg-[#047857]/10 text-[#047857]' : 'bg-[#F4F1E8] text-[#1C2A24]/50'}`}>{t.status}</span>
                  </div>
                  <p className="text-xs text-[#1C2A24]/40">{t.category} &middot; {new Date(t.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>

            {/* Content Area */}
            <div className="md:col-span-2">
              {showNew ? (
                <form onSubmit={submitTicket} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 space-y-4" data-testid="new-ticket-form">
                  <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Submit a Ticket</h2>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                      <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="ticket-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Question</SelectItem>
                        <SelectItem value="billing">Billing & Payments</SelectItem>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="refund">Refund Request</SelectItem>
                        <SelectItem value="account">Account Issue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="ticket-subject" />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={5} required data-testid="ticket-message" />
                  </div>
                  <Button type="submit" disabled={loading} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="submit-ticket-btn">
                    {loading ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </form>
              ) : selectedTicket ? (
                <div className="bg-white rounded-3xl border border-[#E5E0D8] overflow-hidden" data-testid="ticket-detail">
                  <div className="p-6 border-b border-[#E5E0D8] bg-[#F4F1E8]/30">
                    <h2 className="text-lg font-medium" style={{fontFamily:'Outfit'}}>{selectedTicket.subject}</h2>
                    <p className="text-xs text-[#1C2A24]/50 mt-1">{selectedTicket.category} &middot; {selectedTicket.status} &middot; {new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                  <div className="p-6 space-y-4 max-h-[400px] overflow-y-auto">
                    <div className="bg-[#F4F1E8]/50 rounded-2xl p-4">
                      <p className="text-xs text-[#1C2A24]/50 mb-1">{selectedTicket.user_name} (You)</p>
                      <p className="text-sm text-[#1C2A24]/80">{selectedTicket.message}</p>
                    </div>
                    {(selectedTicket.replies || []).map(r => (
                      <div key={r.id} className={`rounded-2xl p-4 ${r.role === 'admin' ? 'bg-[#047857]/5 border border-[#047857]/10' : 'bg-[#F4F1E8]/50'}`}>
                        <p className="text-xs text-[#1C2A24]/50 mb-1">{r.user_name} {r.role === 'admin' ? '(Support Team)' : ''}</p>
                        <p className="text-sm text-[#1C2A24]/80">{r.message}</p>
                        <p className="text-xs text-[#1C2A24]/30 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-[#E5E0D8] flex gap-3">
                    <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..."
                      className="rounded-full bg-[#F4F1E8] border-[#E5E0D8]" data-testid="ticket-reply-input"
                      onKeyDown={e => e.key === 'Enter' && sendReply()} />
                    <Button onClick={sendReply} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white shrink-0" data-testid="send-reply-btn">
                      <Send size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center">
                  <MessageSquare size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
                  <p className="text-[#1C2A24]/50">Select a ticket or create a new one</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Contact Form for non-logged-in users */
          <form onSubmit={submitContact} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 max-w-xl mx-auto space-y-4" data-testid="contact-form">
            <div className="flex items-center gap-3 mb-4">
              <Mail size={20} className="text-[#047857]" strokeWidth={1.5} />
              <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Contact Us</h2>
            </div>
            <div>
              <Label>Your Name</Label>
              <Input value={contactForm.name} onChange={e => setContactForm(f => ({...f, name: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="contact-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required data-testid="contact-email" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={contactForm.category} onValueChange={v => setContactForm(f => ({...f, category: v}))}>
                <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={contactForm.message} onChange={e => setContactForm(f => ({...f, message: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={5} required data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="submit-contact-btn">
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
